import { z } from 'zod';

import * as zod from './generated/zod.gen';
import type {
  CreateIndividualBeneficiaryRequestDto,
  CreateOrganizationRequestDto,
} from './generated/types.gen';

export type RegistrationCountry = CreateOrganizationRequestDto['registrationCountry'];
export type BeneficiaryCountry = NonNullable<
  CreateIndividualBeneficiaryRequestDto['address']
>['country'];

export interface CountryOption {
  label: string;
  value: RegistrationCountry;
}

const regionNames =
  typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const registrationCountrySchema = zod.createOrganizationRequestDto.shape.registrationCountry;
const beneficiaryAddressSchema = zod.createIndividualBeneficiaryRequestDto.shape.address;

if (!(registrationCountrySchema instanceof z.ZodEnum)) {
  throw new Error('Expected registrationCountry to be a Zod enum.');
}

if (!(beneficiaryAddressSchema instanceof z.ZodOptional)) {
  throw new Error('Expected beneficiary address to be optional.');
}

const beneficiaryCountrySchema = beneficiaryAddressSchema.unwrap().shape.country;

if (!(beneficiaryCountrySchema instanceof z.ZodEnum)) {
  throw new Error('Expected beneficiary country to be a Zod enum.');
}

const registrationCountryOptions = Object.freeze(
  registrationCountrySchema.options
    .map((value) => ({
      label: regionNames?.of(value) ?? value,
      value,
    }))
    .sort((left, right) => left.label.localeCompare(right.label)),
);

const beneficiaryCountryOptions = Object.freeze(
  beneficiaryCountrySchema.options
    .map((value) => ({
      label: regionNames?.of(value) ?? value,
      value,
    }))
    .sort((left, right) => left.label.localeCompare(right.label)),
);

export const getRegistrationCountryOptions = (allowedCountryCodes?: readonly string[]) => {
  if (allowedCountryCodes === undefined) {
    return registrationCountryOptions;
  }

  const allowedCountryCodeSet = new Set(
    allowedCountryCodes.map((countryCode) => countryCode.trim().toUpperCase()),
  );

  return Object.freeze(
    registrationCountryOptions.filter(({ value }) => allowedCountryCodeSet.has(value)),
  );
};

export const getBeneficiaryCountryOptions = () => beneficiaryCountryOptions;
